/**
 * Shared, pure row-model helpers for the virtualised Assign-to-venues grids
 * (AssignTableVenues / AssignTableExpenses).
 *
 * ⚠ CRITICAL (IB-751 risk R3): `handleQuantityChange(value, expenseKey, …)`,
 * `props.totals.expenses[…]` and `props.showExpense[…]` are ALL keyed by the
 * ORIGINAL index of the expense inside the unfiltered `expenses` array. When we
 * skip collapsed / deleted rows for virtualisation we must therefore carry the
 * original index on every retained row and use it for every callback / lookup —
 * never the post-filter position. Re-indexing after a filter or an edit would
 * route the edit (and the totals) to the wrong expense.
 */

/**
 * Build the list of rows the venues-view body should render, in order, skipping
 * collapsed expense rows (`!showExpense[i]`) and soft-deleted rows (`deleted`),
 * while preserving each retained row's original `expenses` index.
 *
 * Divider rows are always retained (unless deleted) — the divider itself is the
 * group header and stays visible; only the expense rows beneath it collapse.
 *
 * @param {Array}  expenses    The full, unfiltered expenses array (from props).
 * @param {Array}  showExpense Per-original-index visibility flags.
 * @returns {Array<{expense: object, expenseIndex: number, isDivider: boolean}>}
 */
export function buildVisibleRows(expenses, showExpense) {
    if (!Array.isArray(expenses)) {
        return [];
    }

    const rows = [];
    for (let expenseIndex = 0; expenseIndex < expenses.length; expenseIndex++) {
        const expense = expenses[expenseIndex];
        if (!expense || expense.deleted) {
            continue;
        }

        const isDivider = expense.type === 'template_divider';
        if (!isDivider && showExpense && showExpense[expenseIndex] === false) {
            continue;
        }

        rows.push({ expense, expenseIndex, isDivider });
    }

    return rows;
}
